import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";

import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import { Box, IconButton, Tooltip, useTheme } from "@mui/material";

import { getSelectionMode } from "../../../../../../components/common/chart-plugins/selection-zoom/geometry";
import type { IChartZoomState } from "../../../../../../components/common/chart-plugins/selection-zoom";
import { CASCADE_SORT_MODE, type TCascadeSortMode } from "../../../multi-analysis/types";
import type { IChartZoomOverview } from "../../../scheme/components/analytics/components/chart-zoom";
import { panChartOverview } from "../../../scheme/components/analytics/components/chart-zoom-overview-geometry";
import { ChartZoomOverview } from "../../../scheme/components/analytics/components/chart-zoom-overview";
import { parseCascadeWallClock, type ICascadeSpectrum } from "./model";
import {
  cascadeDisplayFrequency,
  cascadeFrequencyHz,
  cascadeHarmonicFrequencies,
  cascadeSubharmonicFrequencies,
  interpolateCascadeAmplitude,
} from "./tools";
import {
  clampCascadeAxisWindow,
  createCascadeProjector,
  decimateCascadeTrace,
  measureCascadeAmplitude,
  measureCascadeFrequency,
  nearestCascadeSample,
  nextCascadeAxisWindow,
  cascadeScaleTicks,
  niceCascadeTicks,
  niceTimeTicks,
  normalizeCascadeDepths,
  spacedScreenLabels,
  type ICascadeAxisWindow,
  type ICascadeProjector,
} from "./plot";
import { CASCADE_TOOLTIP_COLORS, getCascadeTooltipLines, getCascadeTooltipPosition } from "./tooltip-utils";

const captureWaterfallOverview = (
  canvas: HTMLCanvasElement,
  projector: ICascadeProjector,
): IChartZoomOverview | null => {
  const width = projector.plotRight - projector.plotLeft;
  const height = projector.plotBottom - projector.plotTop;
  const ratio = canvas.clientWidth > 0 ? canvas.width / canvas.clientWidth : window.devicePixelRatio || 1;
  if (!(width > 0) || !(height > 0) || !(ratio > 0)) return null;
  const snapshot = document.createElement("canvas");
  snapshot.width = Math.max(1, Math.round(width * ratio));
  snapshot.height = Math.max(1, Math.round(height * ratio));
  const snapshotContext = snapshot.getContext("2d");
  if (!snapshotContext) return null;
  snapshotContext.drawImage(
    canvas,
    projector.plotLeft * ratio,
    projector.plotTop * ratio,
    width * ratio,
    height * ratio,
    0,
    0,
    snapshot.width,
    snapshot.height,
  );
  return {
    imageUrl: snapshot.toDataURL(),
    width,
    height,
    domain: {
      x: { min: projector.window.x.min, max: projector.window.x.max },
      y: { y: { min: projector.window.y.min, max: projector.window.y.max } },
    },
    scaleTicks: {},
  };
};

export interface ICascadeWaterfallChartProps {
  spectra: readonly ICascadeSpectrum[];
  cursorKey: string | null;
  onCursorChange: (key: string, amplitude?: number, frequencyHz?: number | null, rpm?: number | null) => void;
  formatValue: (value: number, unitCode: number | null) => string;
  sortMode: TCascadeSortMode;
  display3D: boolean;
  hiddenSeriesKeys?: readonly string[];
  markerMode?: boolean;
  markerFrequencyHz?: number | null;
  markerRpm?: number | null;
  harmonicCursors?: boolean;
  subHarmonicCursors?: boolean;
  frequencyProfile?: boolean;
  profileFrequencyHz?: number | null;
  profileRpm?: number | null;
}

interface IPreparedTrace {
  index: number;
  key: string;
  color: string;
  points: readonly { x: number; y: number }[];
  source: ICascadeSpectrum;
}

interface IWaterfallZoom {
  x: ICascadeAxisWindow | null;
  y: ICascadeAxisWindow | null;
}

const formatWaterfallTime = (timestamp: number, span: number): string => {
  const date = new Date(timestamp);
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  const seconds = String(date.getUTCSeconds()).padStart(2, "0");
  if (span < 86_400_000) return `${hours}:${minutes}:${seconds}`;
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  return `${day}.${month}.${date.getUTCFullYear()} ${hours}:${minutes}`;
};

export const CascadeWaterfallChart = ({
  spectra,
  cursorKey,
  onCursorChange,
  formatValue,
  sortMode,
  display3D,
  hiddenSeriesKeys,
  markerMode = false,
  markerFrequencyHz = null,
  markerRpm = null,
  harmonicCursors = false,
  subHarmonicCursors = false,
  frequencyProfile = false,
  profileFrequencyHz = null,
  profileRpm = null,
}: ICascadeWaterfallChartProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const hintId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tooltipHoveredRef = useRef(false);
  const tooltipFocusedRef = useRef(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef(cursorKey);
  const onCursorChangeRef = useRef(onCursorChange);
  const hoverRef = useRef<{ hz: number; rpm: number | null } | null>(null);
  const suppressClickRef = useRef(false);
  const overviewRef = useRef<IChartZoomOverview | null>(null);
  const zoomRef = useRef<IWaterfallZoom | null>(null);
  const [zoom, setZoom] = useState<IWaterfallZoom | null>(null);
  const [overview, setOverview] = useState<IChartZoomOverview | null>(null);
  zoomRef.current = zoom;
  cursorRef.current = cursorKey;
  onCursorChangeRef.current = onCursorChange;
  const chartLabel = formatMessage({
    id: sortMode === CASCADE_SORT_MODE.TIME ? "pages.speed.cascade.waterfall" : "pages.speed.cascade.cascade",
  });
  const keyboardHint = formatMessage({ id: "pages.speed.cascade.chartKeyboardHint" });
  const visibleSpectra = useMemo(
    () => spectra.filter(item => !hiddenSeriesKeys?.includes(item.seriesKey)),
    [hiddenSeriesKeys, spectra],
  );

  useEffect(() => {
    overviewRef.current = null;
    setOverview(null);
    setZoom(null);
  }, [spectra]);
  const overviewZoom = useMemo<IChartZoomState | null>(() => {
    if (!overview || !zoom) return null;
    const x = zoom.x ?? overview.domain.x;
    const y = zoom.y ?? overview.domain.y.y;
    if (!x || !y) return null;
    return { x, y: { y } };
  }, [overview, zoom]);
  const panZoom = useCallback((initial: IChartZoomState, deltaX: number, deltaY: number) => {
    const source = overviewRef.current;
    if (!source) return;
    const next = panChartOverview(initial, source.domain, deltaX, deltaY);
    if (!next) return;
    const coversDomain = (
      window: { min: number; max: number } | undefined,
      domain: { min: number; max: number } | undefined,
    ) => {
      if (!window || !domain) return true;
      const span = domain.max - domain.min;
      return window.min <= domain.min + span * 1e-6 && window.max >= domain.max - span * 1e-6;
    };
    const x = coversDomain(next.x, source.domain.x) ? null : next.x ?? null;
    const y = coversDomain(next.y?.y, source.domain.y.y) ? null : next.y?.y ?? null;
    setZoom(x || y ? { x, y } : null);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !container || !context) return;

    let traces: IPreparedTrace[] = [];
    let projector: ICascadeProjector | null = null;
    const textColor = theme.palette.text.secondary;
    const gridColor = theme.palette.divider;
    const cursorColor = theme.palette.common.black;
    const fontFamily = theme.typography.fontFamily ?? "sans-serif";
    const paddingRight = sortMode === CASCADE_SORT_MODE.TIME ? 136 : 78;

    const hideTooltip = () => {
      if (!tooltipHoveredRef.current && !tooltipFocusedRef.current && tooltipRef.current)
        tooltipRef.current.style.display = "none";
    };

    const showTooltip = (trace: IPreparedTrace, point: { x: number; y: number }, anchor: { x: number; y: number }) => {
      const element = tooltipRef.current;
      if (!element) return;
      const frequency = `${formatValue(point.x, trace.source.frequencyUnitCode)} ${trace.source.frequencyUnits}`.trim();
      const lines = [
        ...getCascadeTooltipLines(
          {
            pointName: trace.source.pointName,
            measureName: trace.source.measureName,
            date: trace.source.date,
            value: point.y,
            unitCode: trace.source.unitCode,
            units: trace.source.units,
          },
          {
            point: formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.point" }),
            measure: formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.measure" }),
            dateTime: formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.dateTime" }),
            value: formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.value" }),
          },
          formatValue,
        ),
        `${formatMessage({ id: "pages.speed.cascade.frequency" })}: ${frequency}`,
      ];
      element.replaceChildren(
        ...lines.map((line, row) => {
          const text = document.createElement("div");
          text.textContent = line;
          text.style.fontWeight = row === 0 ? "600" : "400";
          text.style.color = row === 0 ? CASCADE_TOOLTIP_COLORS.title : CASCADE_TOOLTIP_COLORS.body;
          return text;
        }),
      );
      element.style.display = "block";
      const position = getCascadeTooltipPosition(
        anchor,
        { width: element.offsetWidth, height: element.offsetHeight },
        { width: container.clientWidth, height: container.clientHeight },
      );
      element.style.left = `${position.x}px`;
      element.style.top = `${position.y}px`;
    };

    const draw = () => {
      const width = container.clientWidth;
      const height = container.clientHeight;
      if (width <= 0 || height <= 0) return;
      const ratio = window.devicePixelRatio || 1;
      const bitmapWidth = Math.floor(width * ratio);
      const bitmapHeight = Math.floor(height * ratio);
      if (canvas.width !== bitmapWidth || canvas.height !== bitmapHeight) {
        canvas.width = bitmapWidth;
        canvas.height = bitmapHeight;
      }
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.clearRect(0, 0, width, height);
      traces = [];
      projector = null;
      if (!visibleSpectra.length) return;

      const fullFrequency = measureCascadeFrequency(visibleSpectra);
      const fullAmplitude = measureCascadeAmplitude(visibleSpectra);
      const frequency = clampCascadeAxisWindow(fullFrequency, zoom?.x ?? null);
      const amplitudeWindow = clampCascadeAxisWindow(
        { min: fullAmplitude.floor, max: fullAmplitude.peak },
        zoom?.y ?? null,
      );
      const amplitude = { floor: amplitudeWindow.min, peak: amplitudeWindow.max };
      const byRotation = sortMode === CASCADE_SORT_MODE.RPM;
      const thirdValues = visibleSpectra.map(spectrum =>
        byRotation ? spectrum.rotationFrequencyRpm : parseCascadeWallClock(spectrum.date),
      );
      const depths = normalizeCascadeDepths(thirdValues);
      let thirdMin = Number.POSITIVE_INFINITY;
      let thirdMax = Number.NEGATIVE_INFINITY;
      for (const value of thirdValues) {
        if (value === null || !Number.isFinite(value)) continue;
        if (value < thirdMin) thirdMin = value;
        if (value > thirdMax) thirdMax = value;
      }
      if (!Number.isFinite(thirdMin)) {
        thirdMin = 0;
        thirdMax = 1;
      }
      const plot = createCascadeProjector({
        display3D,
        width,
        height,
        count: visibleSpectra.length,
        xMin: frequency.min,
        xMax: frequency.max,
        amplitudeFloor: amplitude.floor,
        amplitudePeak: amplitude.peak,
        paddingRight,
        depths,
      });
      projector = plot;
      traces = visibleSpectra.map((spectrum, index) => ({
        index,
        key: spectrum.key,
        color: spectrum.color,
        points: decimateCascadeTrace(
          spectrum.items.filter(item => item.x >= frequency.min && item.x <= frequency.max),
          700,
        ),
        source: spectrum,
      }));
      const lastIndex = Math.max(visibleSpectra.length - 1, 0);

      context.save();
      context.beginPath();
      context.rect(0, 0, width, plot.plotBottom);
      context.clip();
      context.strokeStyle = gridColor;
      context.lineWidth = 1;
      const corner = (frequencyValue: number, amplitudeValue: number, index: number) =>
        plot.project(frequencyValue, amplitudeValue, index);
      const strokeLoop = (points: { x: number; y: number }[]) => {
        context.beginPath();
        points.forEach((point, index) =>
          index === 0 ? context.moveTo(point.x, point.y) : context.lineTo(point.x, point.y),
        );
        context.closePath();
        context.stroke();
      };
      strokeLoop([
        corner(frequency.min, amplitude.floor, 0),
        corner(frequency.max, amplitude.floor, 0),
        corner(frequency.max, amplitude.floor, lastIndex),
        corner(frequency.min, amplitude.floor, lastIndex),
      ]);
      if (display3D) {
        strokeLoop([
          corner(frequency.min, amplitude.floor, 0),
          corner(frequency.min, amplitude.peak, 0),
          corner(frequency.max, amplitude.peak, 0),
          corner(frequency.max, amplitude.floor, 0),
        ]);
        context.beginPath();
        const backLeftFloor = corner(frequency.min, amplitude.floor, lastIndex);
        const backLeftPeak = corner(frequency.min, amplitude.peak, lastIndex);
        context.moveTo(backLeftFloor.x, backLeftFloor.y);
        context.lineTo(backLeftPeak.x, backLeftPeak.y);
        context.stroke();
      }
      niceCascadeTicks(frequency.min, frequency.max).forEach(tick => {
        const start = corner(tick, amplitude.floor, 0);
        const end = corner(tick, amplitude.floor, lastIndex);
        context.beginPath();
        context.moveTo(start.x, start.y);
        context.lineTo(end.x, end.y);
        context.stroke();
      });

      const paintTrace = (trace: IPreparedTrace, active: boolean) => {
        if (!trace.points.length) return;
        context.beginPath();
        trace.points.forEach((point, index) => {
          const screen = plot.project(point.x, point.y, trace.index);
          if (index === 0) context.moveTo(screen.x, screen.y);
          else context.lineTo(screen.x, screen.y);
        });
        context.strokeStyle = active ? cursorColor : trace.color;
        context.lineWidth = active ? 2.2 : 1;
        context.lineJoin = "round";
        context.lineCap = "round";
        context.stroke();
      };
      const ordered = display3D ? [...traces].reverse() : traces;
      if (zoom?.y) {
        context.save();
        context.beginPath();
        context.rect(plot.plotLeft, plot.plotTop, plot.plotRight - plot.plotLeft, plot.plotBottom - plot.plotTop);
        context.clip();
      }
      ordered.forEach(trace => {
        if (trace.key !== cursorRef.current) paintTrace(trace, false);
      });
      ordered.forEach(trace => {
        if (trace.key === cursorRef.current) paintTrace(trace, true);
      });
      if (zoom?.y) context.restore();

      const axisUnit = visibleSpectra[0]?.frequencyUnitCode ?? 3;
      const strokeFrequency = (displayX: number, color: string, dash: number[]) => {
        if (!Number.isFinite(displayX) || displayX < frequency.min || displayX > frequency.max) return;
        context.save();
        context.strokeStyle = color;
        context.lineWidth = 1;
        context.setLineDash(dash);
        if (!display3D) {
          const anchor = plot.projectDepth(displayX, amplitude.floor, 0);
          context.beginPath();
          context.moveTo(anchor.x, plot.plotTop);
          context.lineTo(anchor.x, anchor.y);
          context.stroke();
        } else {
          context.beginPath();
          let started = false;
          traces.forEach(trace => {
            const amplitudeAt = interpolateCascadeAmplitude(trace.points, displayX);
            if (amplitudeAt === null) return;
            const point = plot.project(displayX, amplitudeAt, trace.index);
            if (!started) {
              context.moveTo(point.x, point.y);
              started = true;
            } else context.lineTo(point.x, point.y);
          });
          if (started) context.stroke();
        }
        context.restore();
      };
      const guideHz = markerMode && markerFrequencyHz != null ? markerFrequencyHz : hoverRef.current?.hz ?? null;
      const guideRpm = markerMode && markerFrequencyHz != null ? markerRpm : hoverRef.current?.rpm ?? null;
      const guideX =
        (harmonicCursors || subHarmonicCursors) && guideHz != null
          ? cascadeDisplayFrequency(guideHz, axisUnit, guideRpm)
          : null;
      if (guideX != null && subHarmonicCursors) {
        cascadeSubharmonicFrequencies(guideX).forEach(frequency => strokeFrequency(frequency, "rgb(0, 0, 255)", []));
      }
      if (guideX != null && harmonicCursors) {
        cascadeHarmonicFrequencies(guideX).forEach(frequency => strokeFrequency(frequency, "rgb(253, 0, 0)", []));
      }
      const profileX =
        frequencyProfile && profileFrequencyHz != null
          ? cascadeDisplayFrequency(profileFrequencyHz, axisUnit, profileRpm)
          : null;
      if (profileX != null) strokeFrequency(profileX, "#ef6c00", [4, 3]);
      const markerX =
        markerMode && markerFrequencyHz != null
          ? cascadeDisplayFrequency(markerFrequencyHz, axisUnit, markerRpm)
          : null;
      if (markerX != null) strokeFrequency(markerX, "#1976d2", []);
      else if (markerMode && hoverRef.current) {
        const hoverX = cascadeDisplayFrequency(hoverRef.current.hz, axisUnit, hoverRef.current.rpm);
        if (hoverX != null) strokeFrequency(hoverX, "rgba(25, 118, 210, 0.55)", [3, 3]);
      }
      context.restore();

      context.fillStyle = textColor;
      context.font = `11px ${fontFamily}`;
      context.textAlign = "center";
      context.textBaseline = "top";
      niceCascadeTicks(frequency.min, frequency.max).forEach(tick => {
        const point = plot.projectDepth(tick, amplitude.floor, 0);
        context.fillText(formatValue(tick, visibleSpectra[0]?.frequencyUnitCode ?? null), point.x, plot.plotBottom + 4);
      });
      context.fillText(
        `${formatMessage({ id: "pages.speed.cascade.frequency" })}, ${visibleSpectra[0]?.frequencyUnits ?? ""}`,
        (plot.plotLeft + plot.plotRight) / 2,
        height - 16,
      );

      context.textAlign = "right";
      context.textBaseline = "middle";
      spacedScreenLabels(
        cascadeScaleTicks(amplitude.floor, amplitude.peak).map(value => ({
          ...plot.projectDepth(frequency.min, value, 0),
          value,
        })),
        16,
      ).forEach(tick => {
        context.fillText(formatValue(tick.value, visibleSpectra[0]?.unitCode ?? null), plot.plotLeft - 6, tick.y);
      });
      context.save();
      context.translate(14, (plot.plotTop + plot.plotBottom) / 2);
      context.rotate(-Math.PI / 2);
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.fillText(
        `${formatMessage({ id: "pages.speed.cascade.value" })}, ${visibleSpectra[0]?.units ?? ""}`,
        0,
        0,
      );
      context.restore();

      const thirdTitle =
        sortMode === CASCADE_SORT_MODE.RPM
          ? formatMessage({ id: "pages.scheme.analytics.rotationFrequencyRpmUnit" })
          : formatMessage({ id: "pages.multiAnalysis.timePanel.tooltip.dateTime" });
      context.textBaseline = "middle";
      const thirdSpan = thirdMax - thirdMin;
      const thirdTicks = byRotation ? cascadeScaleTicks(thirdMin, thirdMax) : niceTimeTicks(thirdMin, thirdMax);
      const depthEnd = plot.projectDepth(frequency.max, amplitude.floor, 1);
      spacedScreenLabels(
        thirdTicks.map(tick => {
          const depth = thirdSpan > 0 ? (tick - thirdMin) / thirdSpan : 0;
          return { ...plot.projectDepth(frequency.max, amplitude.floor, depth), tick };
        }),
        display3D ? 72 : 18,
      ).forEach(tick => {
        const label = byRotation ? formatValue(tick.tick, null) : formatWaterfallTime(tick.tick, thirdSpan);
        context.textAlign = "left";
        context.fillText(label, Math.min(tick.x + 6, width - 8), tick.y);
      });
      context.save();
      if (display3D) {
        context.translate(depthEnd.x + 8, depthEnd.y - 22);
      } else {
        context.translate(width - 12, (plot.plotTop + plot.plotBottom) / 2);
        context.rotate(-Math.PI / 2);
      }
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.fillText(thirdTitle, 0, 0);
      context.restore();
    };

    const pointer = (event: MouseEvent) => {
      if (!projector) return null;
      const bounds = canvas.getBoundingClientRect();
      return nearestCascadeSample(traces, projector.project, event.clientX - bounds.left, event.clientY - bounds.top);
    };
    const hoverFrom = (found: { index: number; frequency: number } | null) => {
      const trace = found ? traces.find(item => item.index === found.index) : null;
      if (!found || !trace) return null;
      const hz = cascadeFrequencyHz(found.frequency, trace.source.frequencyUnitCode, trace.source.rotationFrequencyRpm);
      return hz == null ? null : { hz, rpm: trace.source.rotationFrequencyRpm };
    };
    const activeTrace = () => traces.find(item => item.key === cursorRef.current) ?? traces[traces.length - 1] ?? null;
    const axisHover = (event: MouseEvent) => {
      if (!projector || !(markerMode || harmonicCursors || subHarmonicCursors || frequencyProfile)) return null;
      const bounds = canvas.getBoundingClientRect();
      const frequency = projector.frequencyAt(event.clientX - bounds.left);
      const trace = activeTrace();
      if (frequency == null || !trace) return null;
      const hz = cascadeFrequencyHz(frequency, trace.source.frequencyUnitCode, trace.source.rotationFrequencyRpm);
      return hz == null ? null : { trace, frequency, hz, rpm: trace.source.rotationFrequencyRpm };
    };
    const overlay = overlayRef.current;
    let dragStart: { x: number; y: number } | null = null;
    const localPoint = (event: MouseEvent) => {
      const bounds = canvas.getBoundingClientRect();
      return { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
    };
    const hideOverlay = () => {
      if (overlay) overlay.style.display = "none";
    };
    function onDragMove(event: MouseEvent) {
      if (!dragStart || !overlay) return;
      const point = localPoint(event);
      const left = Math.min(dragStart.x, point.x);
      const top = Math.min(dragStart.y, point.y);
      overlay.style.display = "block";
      overlay.style.transform = `translate3d(${left}px, ${top}px, 0)`;
      overlay.style.width = `${Math.max(1, Math.abs(point.x - dragStart.x))}px`;
      overlay.style.height = `${Math.max(1, Math.abs(point.y - dragStart.y))}px`;
      hideTooltip();
    }
    function onDragEnd(event: MouseEvent) {
      const start = dragStart;
      dragStart = null;
      window.removeEventListener("mousemove", onDragMove);
      window.removeEventListener("mouseup", onDragEnd);
      hideOverlay();
      if (!start || !projector) return;
      const point = localPoint(event);
      const mode = getSelectionMode(start.x, start.y, point.x, point.y, 8);
      if (!mode) return;
      suppressClickRef.current = true;
      const from = projector.unmap(start.x, start.y);
      const to = projector.unmap(point.x, point.y);
      const xWindow = mode === "y" ? null : nextCascadeAxisWindow(projector.window.x, from.frequency, to.frequency);
      const yWindow = mode === "x" ? null : nextCascadeAxisWindow(projector.window.y, from.amplitude, to.amplitude);
      if (!xWindow && !yWindow) return;
      if (!overviewRef.current && !zoomRef.current && canvas) {
        const captured = captureWaterfallOverview(canvas, projector);
        if (captured) {
          overviewRef.current = captured;
          setOverview(captured);
        }
      }
      setZoom(previous => {
        const next = { x: xWindow ?? previous?.x ?? null, y: yWindow ?? previous?.y ?? null };
        return next.x || next.y ? next : null;
      });
    }
    const detachDrag = () => {
      window.removeEventListener("mousemove", onDragMove);
      window.removeEventListener("mouseup", onDragEnd);
    };
    const onMouseDown = (event: MouseEvent) => {
      suppressClickRef.current = false;
      if (event.button === 1) {
        event.preventDefault();
        setZoom(null);
        return;
      }
      if (event.button !== 0 || !projector) return;
      const point = localPoint(event);
      if (
        point.x < projector.plotLeft ||
        point.x > projector.plotRight ||
        point.y < projector.plotTop ||
        point.y > projector.plotBottom
      )
        return;
      dragStart = point;
      event.preventDefault();
      window.addEventListener("mousemove", onDragMove);
      window.addEventListener("mouseup", onDragEnd);
    };
    const onClick = (event: MouseEvent) => {
      if (suppressClickRef.current) {
        suppressClickRef.current = false;
        return;
      }
      const found = pointer(event);
      const trace = found ? traces[found.index] : null;
      if (found && trace) {
        const hover = hoverFrom(found);
        onCursorChangeRef.current(trace.key, found.amplitude, hover?.hz ?? null, hover?.rpm ?? null);
        return;
      }
      const axis = axisHover(event);
      if (!axis) return;
      onCursorChangeRef.current(
        axis.trace.key,
        interpolateCascadeAmplitude(axis.trace.source.items, axis.frequency) ?? undefined,
        axis.hz,
        axis.rpm,
      );
    };
    const onMove = (event: MouseEvent) => {
      if (dragStart) {
        hideTooltip();
        return;
      }
      const found = pointer(event);
      const trace = found ? traces.find(item => item.index === found.index) : null;
      const sampleHover = hoverFrom(found);
      const axis = sampleHover ? null : axisHover(event);
      const hover = sampleHover ?? (axis ? { hz: axis.hz, rpm: axis.rpm } : null);
      const hoverChanged = hoverRef.current?.hz !== hover?.hz || hoverRef.current?.rpm !== hover?.rpm;
      hoverRef.current = hover;
      if (hoverChanged && (markerMode || harmonicCursors || subHarmonicCursors || frequencyProfile)) draw();
      if (!found || !trace) {
        hideTooltip();
        return;
      }
      showTooltip(trace, { x: found.frequency, y: found.amplitude }, { x: found.x, y: found.y });
    };
    const onLeave = () => {
      if (hoverRef.current) {
        hoverRef.current = null;
        if (markerMode || harmonicCursors || subHarmonicCursors || frequencyProfile) draw();
      }
      hideTooltip();
    };

    canvas.addEventListener("mousedown", onMouseDown);
    canvas.addEventListener("click", onClick);
    canvas.addEventListener("mousemove", onMove);
    canvas.addEventListener("mouseleave", onLeave);
    const observer = new ResizeObserver(draw);
    observer.observe(container);
    draw();
    return () => {
      observer.disconnect();
      detachDrag();
      hideOverlay();
      canvas.removeEventListener("mousedown", onMouseDown);
      canvas.removeEventListener("click", onClick);
      canvas.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("mouseleave", onLeave);
      hideTooltip();
    };
  }, [
    cursorKey,
    display3D,
    formatMessage,
    formatValue,
    frequencyProfile,
    harmonicCursors,
    markerFrequencyHz,
    markerMode,
    markerRpm,
    profileFrequencyHz,
    profileRpm,
    sortMode,
    subHarmonicCursors,
    theme.palette,
    theme.typography.fontFamily,
    visibleSpectra,
    zoom,
  ]);

  return (
    <Box
      ref={containerRef}
      data-cascade-chart="waterfall"
      data-cascade-display={display3D ? "3d" : "2d"}
      data-cascade-cursor-key={cursorKey ?? ""}
      data-cascade-zoomed={zoom ? "true" : "false"}
      onMouseOutCapture={event => {
        const next = event.relatedTarget;
        tooltipHoveredRef.current = next instanceof Node && Boolean(tooltipRef.current?.contains(next));
      }}
      sx={{ flex: 1, minWidth: 0, minHeight: 0, position: "relative", overflow: "hidden" }}
    >
      <ChartZoomOverview overview={overview} zoomState={overviewZoom} onPan={panZoom} />
      {zoom ? (
        <Tooltip title={formatMessage({ id: "pages.scheme.analytics.resetZoom" })}>
          <IconButton
            size="small"
            aria-label={formatMessage({ id: "pages.scheme.analytics.resetZoom" })}
            onClick={() => setZoom(null)}
            sx={{ position: "absolute", top: 4, left: 4, zIndex: 2, bgcolor: "background.paper" }}
          >
            <ZoomOutMapIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      <Box
        ref={overlayRef}
        data-cascade-zoom-selection=""
        sx={{
          display: "none",
          position: "absolute",
          left: 0,
          top: 0,
          zIndex: 2,
          boxSizing: "border-box",
          border: "1px dashed rgba(25, 65, 169, 0.75)",
          bgcolor: "rgba(25, 65, 169, 0.12)",
          pointerEvents: "none",
          transformOrigin: "0 0",
        }}
      />
      <canvas
        ref={canvasRef}
        role="img"
        tabIndex={0}
        aria-label={chartLabel}
        aria-describedby={hintId}
        onDoubleClick={() => setZoom(null)}
        onKeyDown={event => {
          if (event.altKey || event.ctrlKey || event.metaKey) return;
          if (event.key === "Escape") {
            setZoom(null);
            return;
          }
          if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return;
          const current = visibleSpectra.findIndex(spectrum => spectrum.key === cursorKey);
          const nextIndex =
            event.key === "ArrowLeft"
              ? Math.max(0, current < 0 ? visibleSpectra.length - 1 : current - 1)
              : Math.min(visibleSpectra.length - 1, current + 1);
          const next = visibleSpectra[nextIndex];
          if (next) {
            event.preventDefault();
            onCursorChange(next.key);
          }
        }}
        style={{
          position: "absolute",
          inset: 0,
          display: "block",
          width: "100%",
          height: "100%",
          cursor: markerMode || frequencyProfile ? "crosshair" : undefined,
        }}
      >
        {chartLabel}
      </canvas>
      <Box
        ref={tooltipRef}
        role="tooltip"
        tabIndex={0}
        data-cascade-tooltip="waterfall"
        onMouseEnter={() => {
          tooltipHoveredRef.current = true;
        }}
        onMouseLeave={() => {
          tooltipHoveredRef.current = false;
          if (!tooltipFocusedRef.current && tooltipRef.current) tooltipRef.current.style.display = "none";
        }}
        onFocus={() => {
          tooltipFocusedRef.current = true;
        }}
        onBlur={() => {
          tooltipFocusedRef.current = false;
          if (!tooltipHoveredRef.current && tooltipRef.current) tooltipRef.current.style.display = "none";
        }}
        onKeyDown={event => {
          if (event.key === "Escape") {
            event.stopPropagation();
            tooltipFocusedRef.current = false;
            tooltipHoveredRef.current = false;
            if (tooltipRef.current) tooltipRef.current.style.display = "none";
            canvasRef.current?.focus();
          }
        }}
        sx={{
          display: "none",
          position: "absolute",
          pointerEvents: "auto",
          zIndex: 3,
          maxWidth: "min(360px, calc(100% - 8px))",
          maxHeight: "calc(100% - 8px)",
          overflowY: "auto",
          overscrollBehavior: "contain",
          boxSizing: "border-box",
          p: 1,
          borderRadius: 1,
          bgcolor: CASCADE_TOOLTIP_COLORS.background,
          color: CASCADE_TOOLTIP_COLORS.body,
          fontSize: 12,
          fontFamily: theme.typography.fontFamily,
          lineHeight: 1.5,
          overflowWrap: "anywhere",
          boxShadow: 3,
        }}
      />
      <Box
        id={hintId}
        component="span"
        sx={{
          position: "absolute",
          width: 1,
          height: 1,
          overflow: "hidden",
          clipPath: "inset(50%)",
        }}
      >
        {keyboardHint}
      </Box>
    </Box>
  );
};
